"use client";

import { useProcessStore } from "@/store/useProcessStore";
import { subscribeToRealtimeMessages } from "@/features/realtime/bus";
import { getNftDetail, isNftListed } from "@/features/asset/api";
import { getOffers } from "./api";
import { waitForChainSettlement, type ChainWorkMode } from "./chain-settlement";

/**
 * HANDING A CHAIN LEG TO THE DOCK, INSTEAD OF HOLDING A DIALOG OPEN FOR IT.
 *
 * =========================================================================
 * WHAT CHANGED, AND WHY
 * =========================================================================
 *
 * All five marketplace actions used to `await` the chain inside the dialog
 * that started them: the user pressed List / Place offer / Accept and then
 * watched a spinner, in front of the page, for as long as the chain took.
 * That was right about ONE thing — the POST is not the result — and wrong
 * about everything else. It pinned the user to a modal for work that does not
 * need them, it could not survive a navigation, and when the window ran out it
 * left a dialog saying neither yes nor no.
 *
 * Creating an NFT and creating a collection had already solved this, and the
 * solution is the dock: close the dialog on the POST, refresh the page, and
 * let a card in the corner follow the chain across whatever the user does
 * next. This is that, for the other five.
 *
 * =========================================================================
 * TWO WITNESSES, ONE CARD
 * =========================================================================
 *
 * The SOCKET is the primary one and it does not run through here at all: the
 * card is patched by `useProcessStore.applyEvent`, off the same
 * `user-private-channel-{uuid}` every other card listens to. That path has no
 * dependency on this module or on the page that opened the card, which is what
 * lets a card keep moving after a full reload.
 *
 * The POLL is the second, and it is the only reason this function exists. A
 * socket message can be missed — a reconnect, a tab that was backgrounded
 * while the chain finished — and without a second look the card would spin
 * until the staleness sweep removed it. `waitForChainSettlement` already owns
 * that race, so it is reused verbatim rather than rewritten as a timer.
 *
 * DELIBERATELY NOT AWAITED. The caller closes its dialog immediately; this
 * runs on afterwards, outside React, and survives the component that started
 * it. It cannot survive a page RELOAD — nothing scheduled in this tab can —
 * which is exactly why the socket, and not this, is the primary witness.
 *
 * IT NEVER FAILS A CARD. `pending` means the chain has not reported inside our
 * window, which is not evidence of anything; the card is left active and the
 * store's own sweep decides when it has gone quiet for good. Writing a failure
 * there would tell a seller their listing did not happen when it did.
 */
export function trackChainWork(args: {
  mode: ChainWorkMode;
  /** Shown on the card. The NFT's name, as this locale spells it. */
  title: string;
  image?: string;
  contractAddress: string;
  tokenId: string;
  lang: "fa" | "en";
  /** `listing_id` / `offer_id`, when the POST named one. */
  workId?: number | string | null;
}): string {
  const cardId = useProcessStore.getState().startChainWork({
    mode: args.mode,
    title: args.title,
    image: args.image,
    workId: args.workId ?? null,
  });

  void waitForChainSettlement(
    args.mode,
    { id: args.workId ?? null, nftId: null },
    {
      subscribe: (onMessage) => subscribeToRealtimeMessages(onMessage),
      poll: () => pollChainWork(args),
    },
  )
    .then((result) => {
      if (result === "settled") useProcessStore.getState().settleChainWork(cardId);
    })
    .catch((error) => {
      // A watcher that threw must not take the card with it. The socket is
      // still attached and is the one that usually answers anyway.
      console.error("[chain-work] the settlement watcher threw:", error);
    });

  return cardId;
}

/**
 * The server's own view, for when the socket is late or was missed.
 *
 * ONE READ PER MODE, and each asks the narrowest question it can. Listing
 * reads the NFT, because that is where a listing lives; the three offer modes
 * read the NFT's offers and look for their own id, because an offer's STATUS
 * is what moved.
 */
export async function pollChainWork(args: {
  mode: ChainWorkMode;
  contractAddress: string;
  tokenId: string;
  lang: "fa" | "en";
  workId?: number | string | null;
}): Promise<boolean> {
  // An auction's settlement is not read from an NFT: its card follows the socket, and its page re-reads the auction.
  if (args.mode === "settleAuction" || args.mode === "swap" || args.mode === "aiMetadata") return false;

  if (args.mode === "list" || args.mode === "cancelListing") {
    const detail = await getNftDetail(args.contractAddress, args.tokenId, args.lang);
    const listed = isNftListed(detail);
    // Listing waits for it to become true; cancelling waits for the opposite.
    // One reading of the server's state, two questions.
    return args.mode === "list" ? listed : !listed;
  }

  // Without an id there is nothing to look for, and 'some offer changed' is
  // not an answer about THIS one. The socket remains the only witness.
  if (args.workId == null) return false;

  const offers = await getOffers(args.contractAddress, args.tokenId, { limit: 50 });
  const mine = (offers?.data?.items ?? []).find(
    (item) => String(item.id) === String(args.workId),
  );
  if (!mine) return false;
  const status = String(mine.status ?? "").toLowerCase();

  if (args.mode === "cancelOffer") return status === "cancelled";
  if (args.mode === "acceptOffer") return status === "accepted";
  // PLACING. `submitted` is the status the POST leaves behind — the offer row
  // exists but the chain leg has not reported — so it is NOT an answer here.
  // `success` is. A backend that keeps `submitted` as the final state simply
  // never settles this poll, and the card is left to the socket and the sweep,
  // which is honest rather than wrong.
  return status === "success";
}
